import { useState, useEffect } from 'react';
import { Grid, Button, TextField } from '@material-ui/core';
import { DialogActions, DialogContentText, DialogContent, Dialog, DialogTitle } from '@material-ui/core';
import { MenuItem } from '@material-ui/core/';
import { Alert, AlertTitle } from '@material-ui/lab';
import SuccessDialog from '../Dialogs/SuccessDialog';
import { Location, Company } from '../../models/location.interface';
import { Locations } from '../../api/api';
import { isEmpty } from "lodash";
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    gridSpacer: {
      minHeight: '12px',
    },
  }),
);

export interface IEditLocationDialog {
  open: boolean
  onClose: () => void
  location: Location
  loadAllLocations: () => void
  companyList: Company[]
}

export const EditLocationDialog = ({
  open, onClose, location, loadAllLocations, companyList
}: IEditLocationDialog) => {
  const classes = useStyles(); 
  const [ saved, setSaved ] = useState<boolean>(false);
  const [ serviceError, setServiceError ] = useState<string>('');
  const [ name, setName ] = useState<string>(location.name);
  const [ phoneNumber, setPhoneNumber ] = useState<string>(location.phoneNumber);
  const [ companyId, setCompanyId ] = useState<string>(location.company.id);
  const [ nameError, setNameError ] = useState<string>('');
  const [ phoneNumberError, setPhoneNumberError ] = useState<string>('');
  const [ companyError, setCompanyError ] = useState<string>('')

  useEffect(() => {
    setName(location.name);
    setPhoneNumber(location.phoneNumber);
    setCompanyId(location.company.id);
  },[location])

  const isFormValid = () => {
    validateField('name', name)
    validateField('company', companyId)
    validateField('phoneNumber', phoneNumber)
    
    return isEmpty(companyError) && isEmpty(nameError) && isEmpty(phoneNumberError) 
  }

  const validateField = (id: string, value: string) => {
    if(id === 'name'){
      isEmpty(value) ? setNameError('Required') : setNameError('')
    } else if(id === 'company'){
      isEmpty(value) || value === '-1' ? setCompanyError('Required') : setCompanyError('')
    } else if(id === 'phoneNumber'){
      isEmpty(value) ? setPhoneNumberError('Required') : setPhoneNumberError('')
    }
  }

  const resetFields = () => {
    setServiceError('')
    setName('')
    setPhoneNumber('')
    setCompanyId('')
    setNameError('')
    setPhoneNumberError('')
  }

  const onCloseHandler = () => {
    onClose()
    setSaved(false)
    resetFields();
  }

  const handleSubmitClick = () => {
    if(isFormValid()){
      Locations.updateLocation(location.id, companyId, name, phoneNumber)
        .then((location) => {
          setSaved(true);
          loadAllLocations();
          resetFields();
        }).catch((e) => {
          setServiceError(e.response.data.message)
          setSaved(false);
        })
    }else{
      setSaved(false);
    }
  }

  return (     
    <div>
      <SuccessDialog 
        open={saved && serviceError.length === 0}
        onClose={onCloseHandler}
        title='Edit Location'
        content='Location Updated'
      />
      <Dialog 
        onClose={onCloseHandler} 
        aria-labelledby="add-user-title" 
        aria-describedby="add-user-text"
        open={open}
        fullWidth={true}
        maxWidth={'xs'}
      >
        <DialogTitle id="add-user-title">Edit Location</DialogTitle>
        <DialogContent>
          <DialogContentText id="add-user-text">   
            {serviceError.length > 0 
              &&
              <Grid item xs={12}>
                <Alert severity="error" >
                  <AlertTitle>Error</AlertTitle>
                  {serviceError}
                </Alert>   
                <Grid container className={classes.gridSpacer} />
              </Grid>
            } 
            <TextField
              id="name"
              label="Name"
              fullWidth
              margin="normal"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)} 
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              error={nameError.length > 0}
              helperText={nameError}
              required
            />
            <TextField
              id="phoneNumber"
              label="Phone Number"
              fullWidth
              margin="normal"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)} 
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              error={phoneNumberError.length > 0}
              helperText={phoneNumberError}
              required
            />
            <TextField
              id="company"
              select
              label="Company"
              fullWidth
              margin="normal"
              name="keyType"
              variant="outlined" 
              required
              onChange={(e) => setCompanyId(e.target.value)}
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              value={companyId}
              error={companyError.length > 0}
              helperText={companyError}
            >
              {companyList.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={onClose}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            size="small" 
            onClick={handleSubmitClick}
          >
            Submit 
          </Button>
        </DialogActions>
      </Dialog>   
    </div>
  );
};

export default EditLocationDialog;